// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import {
  Loader2,
  Pencil,
  Plus,
  Save,
  Sparkles,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { InlineDeleteConfirm } from '@/app/components/inline-delete-confirm';
import { BulkActionBar, SelectionCheckbox } from '@/app/components/bulk-action-bar';
import { PageHeader, FormCard } from '@/app/components/page-layout';
import { UnsavedChangesGuard } from '@/app/components/unsaved-changes-guard';
import type { BulkAction } from '@/app/components/bulk-action-bar';
import { useBulkSelection } from '@/app/hooks/useBulkSelection';
import { useUnsavedChanges } from '@/app/hooks/useUnsavedChanges';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  useSkills,
  useCreateSkill,
  useUpdateSkill,
  useDeleteSkill,
  useBulkDeleteSkills,
  useSuggestSkills,
  skillSchema,
  CATEGORIES,
  PROFICIENCIES,
} from '@/app/features/skill';
import type { SkillFormData } from '@/app/features/skill';

type Skill = {
  id: string;
  name: string;
  category: string;
  proficiency?: string;
  years_experience?: number;
  created_at: string;
};

const PROFICIENCY_COLORS: Record<string, string> = {
  beginner:
    'border border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100',
  intermediate:
    'border border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-500 dark:bg-blue-800 dark:text-blue-100',
  advanced:
    'border border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-500 dark:bg-violet-800 dark:text-violet-100',
  expert:
    'border border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-500 dark:bg-amber-800 dark:text-amber-100',
};

const CATEGORY_LABELS: Record<string, string> = {
  technical: 'Technical',
  soft: 'Soft Skills',
  tool: 'Tools',
  certification: 'Certifications',
  language: 'Languages',
};

export function SkillsPage() {
  const [categoryFilter, setCategoryFilter] = useState('');

  const skills = useSkills({
    category: categoryFilter || undefined,
  });
  const createSkill = useCreateSkill();
  const updateSkill = useUpdateSkill();
  const deleteSkill = useDeleteSkill();
  const bulkDeleteSkills = useBulkDeleteSkills();
  const suggestSkills = useSuggestSkills();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<SkillFormData>({
    name: '',
    category: '',
    proficiency: '',
    years_experience: '',
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof SkillFormData, string>>
  >({});

  const unsaved = useUnsavedChanges(formData, { enabled: !!editingId });
  const editFormRef = useRef<HTMLDivElement>(null);

  // AI Suggest state
  type SkillSuggestion = { name: string; category: string; proficiency?: string };
  const [showSuggestPanel, setShowSuggestPanel] = useState(false);
  const [suggestJobTitle, setSuggestJobTitle] = useState('');
  const [includeExperiences, setIncludeExperiences] = useState(true);
  const [includeAccomplishments, setIncludeAccomplishments] = useState(true);
  const [suggestions, setSuggestions] = useState<SkillSuggestion[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(
    new Set(),
  );
  const suggestPanelRef = useRef<HTMLDivElement>(null);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      category: '',
      proficiency: '',
      years_experience: '',
    });
    setFieldErrors({});
    setEditingId(null);
    setIsFormOpen(false);
    unsaved.clear();
  }, [unsaved]);

  function openCreateForm() {
    resetForm();
    setIsFormOpen(true);
  }

  function openEditForm(skill: Skill) {
    unsaved.guardClose(() => {
      const data: SkillFormData = {
        name: skill.name,
        category: skill.category,
        proficiency: skill.proficiency ?? '',
        years_experience:
          skill.years_experience !== undefined
            ? String(skill.years_experience)
            : '',
      };
      setFormData(data);
      unsaved.snapshot(data);
      setFieldErrors({});
      setEditingId(skill.id);
      setIsFormOpen(true);
      // Scroll the inline form into view and focus its first input.
      setTimeout(() => {
        editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        editFormRef.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
      }, 50);
    });
  }

  function handleCloseForm() {
    unsaved.guardClose(resetForm);
  }

  function handleChange(field: keyof SkillFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});

    const result = skillSchema.safeParse(formData);
    if (!result.success) {
      const errors: Partial<Record<keyof SkillFormData, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof SkillFormData;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    const onSuccess = () => {
      resetForm();
      toast.success('Skill saved successfully.');
    };

    if (editingId) {
      updateSkill.mutate({ ...result.data, id: editingId }, { onSuccess });
    } else {
      createSkill.mutate(result.data, { onSuccess });
    }
  }

  function handleDelete(id: string) {
    setDeletingId(id);
    deleteSkill.mutate(id, {
      onSuccess: () => {
        setDeletingId(null);
        if (editingId === id) resetForm();
      },
      onError: () => setDeletingId(null),
    });
  }

  // AI Suggest handlers
  function openSuggestPanel() {
    setSuggestJobTitle('');
    setIncludeExperiences(true);
    setIncludeAccomplishments(true);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setShowSuggestPanel(true);
  }

  function closeSuggestPanel() {
    setShowSuggestPanel(false);
    setSuggestJobTitle('');
    setIncludeExperiences(true);
    setIncludeAccomplishments(true);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
  }

  function handleGenerateSuggestions(e: React.FormEvent) {
    e.preventDefault();
    // At least one context source must be active.
    if (!suggestJobTitle.trim() && !includeExperiences && !includeAccomplishments) return;

    suggestSkills.mutate({
      job_title: suggestJobTitle.trim() || undefined,
      include_experiences: includeExperiences,
      include_accomplishments: includeAccomplishments,
    }, {
      onSuccess: (data) => {
        setSuggestions(data);
        setSelectedSuggestions(new Set());
        // Scroll panel into view after data loads.
        setTimeout(() => {
          suggestPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 50);
      },
    });
  }

  function toggleSuggestion(index: number) {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  function handleAcceptSuggestions() {
    const selected = suggestions.filter((_, i) => selectedSuggestions.has(i));
    if (selected.length === 0) return;

    // Create each selected skill one by one.
    let completed = 0;
    for (const suggestion of selected) {
      createSkill.mutate(
        {
          name: suggestion.name,
          category: suggestion.category,
          proficiency: suggestion.proficiency ?? '',
          years_experience: '',
        },
        {
          onSuccess: () => {
            completed++;
            if (completed === selected.length) {
              closeSuggestPanel();
              toast.success('Skill saved successfully.');
            }
          },
        },
      );
    }
  }

  const isPending = createSkill.isPending || updateSkill.isPending;

  const skillForm = (
    <FormCard className={editingId ? 'mt-0 sm:mt-0' : undefined}>
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold text-foreground">
          {editingId ? 'Edit Skill' : 'New Skill'}
        </h2>
        <button
          type="button"
          onClick={handleCloseForm}
          className="rounded-md p-2 sm:p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Close form"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {(createSkill.error ?? updateSkill.error) && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {(createSkill.error ?? updateSkill.error)?.message}
        </div>
      )}

      <form
        className="mt-4 space-y-4"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="skill-name"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Name
            </label>
            <input
              id="skill-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="React, Python, Leadership..."
              aria-invalid={!!fieldErrors.name}
              aria-describedby={
                fieldErrors.name ? 'skill-name-error' : undefined
              }
            />
            {fieldErrors.name && (
              <p
                id="skill-name-error"
                className="mt-1 text-[13px] text-destructive"
              >
                {fieldErrors.name}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="skill-category"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Category
            </label>
            <select
              id="skill-category"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              aria-invalid={!!fieldErrors.category}
              aria-describedby={
                fieldErrors.category ? 'skill-cat-error' : undefined
              }
            >
              <option value="">Select category</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_LABELS[cat] ?? cat}
                </option>
              ))}
            </select>
            {fieldErrors.category && (
              <p
                id="skill-cat-error"
                className="mt-1 text-[13px] text-destructive"
              >
                {fieldErrors.category}
              </p>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="skill-proficiency"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Proficiency{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <select
              id="skill-proficiency"
              value={formData.proficiency}
              onChange={(e) =>
                handleChange('proficiency', e.target.value)
              }
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">No proficiency level</option>
              {PROFICIENCIES.map((prof) => (
                <option key={prof} value={prof}>
                  {prof.charAt(0).toUpperCase() + prof.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="skill-years"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Years of Experience{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <input
              id="skill-years"
              type="number"
              min={0}
              max={100}
              value={formData.years_experience}
              onChange={(e) =>
                handleChange('years_experience', e.target.value)
              }
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="0"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 border-t border-border pt-4">
          <button
            type="submit"
            disabled={isPending || (!!editingId && !unsaved.hasChanges)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[15px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {editingId ? 'Update' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCloseForm}
            className="rounded-md px-4 py-2 text-[15px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </FormCard>
  );

  // Group skills by category for display.
  const skillList = (skills.data ?? []) as Skill[];
  const groupedSkills = useMemo(() => {
    const groups: Record<string, Skill[]> = {};
    for (const skill of skillList) {
      const cat = skill.category || 'uncategorized';
      if (!groups[cat]) {
        groups[cat] = [];
      }
      groups[cat].push(skill);
    }
    // Sort groups by category order defined in CATEGORIES constant.
    const orderedGroups: [string, Skill[]][] = [];
    for (const cat of CATEGORIES) {
      if (groups[cat]) {
        orderedGroups.push([cat, groups[cat]]);
        delete groups[cat];
      }
    }
    // Any remaining custom categories.
    for (const [cat, items] of Object.entries(groups)) {
      orderedGroups.push([cat, items]);
    }
    return orderedGroups;
  }, [skillList]);

  const allSkillIds = useMemo(() => skillList.map((s) => s.id), [skillList]);
  const bulk = useBulkSelection(allSkillIds);
  const inSelectionMode = !bulk.noneSelected;

  const bulkActions: BulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        label: 'Delete',
        icon: Trash2,
        destructive: true,
        onExecute: async (ids) => {
          await bulkDeleteSkills.mutateAsync(ids);
        },
      },
    ],
    [bulkDeleteSkills],
  );

  if (skills.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (skills.error) {
    return (
      <div>
        <PageHeader title="Skills" />
        <div
          className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          Failed to load skills. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Skills"
        description="Manage your skills with categories and proficiency levels."
      >
        {!isFormOpen && (
          <>
            <button
              type="button"
              onClick={openSuggestPanel}
              className="inline-flex items-center gap-2 rounded-md bg-ai px-3 py-2 text-[14px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ai focus:ring-offset-2"
            >
              <Sparkles className="h-4 w-4" />
              AI Suggest
            </button>
            <button
              type="button"
              onClick={openCreateForm}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <Plus className="h-4 w-4" />
              Add Skill
            </button>
          </>
        )}
      </PageHeader>

      {/* AI Suggest panel */}
      {showSuggestPanel && (
        <div ref={suggestPanelRef} className="mt-4 rounded-lg border border-foreground bg-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-ai" />
              <h2 className="text-[16px] font-semibold text-foreground">
                AI Skill Suggestions
              </h2>
            </div>
            <button
              type="button"
              onClick={closeSuggestPanel}
              className="rounded-md p-2 sm:p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close suggestions"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {suggestions.length === 0 ? (
            <form onSubmit={handleGenerateSuggestions} className="mt-3">
              <p className="text-[14px] text-muted-foreground">
                Select which context to use for suggestions. Existing skills are
                excluded automatically.
              </p>

              {/* Context checkboxes */}
              <div className="mt-4 space-y-2.5">
                <label className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={includeExperiences}
                    onChange={(e) => setIncludeExperiences(e.target.checked)}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <span className="text-[14px] text-foreground">
                    Use work experiences
                  </span>
                </label>
                <label className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={includeAccomplishments}
                    onChange={(e) =>
                      setIncludeAccomplishments(e.target.checked)
                    }
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <span className="text-[14px] text-foreground">
                    Use accomplishments
                  </span>
                </label>
              </div>

              {/* Optional job title */}
              <div className="mt-4">
                <label
                  htmlFor="suggest-skill-job-title"
                  className="mb-1.5 block text-[14px] font-medium text-foreground"
                >
                  Job Title{' '}
                  <span className="font-normal text-muted-foreground">
                    (optional)
                  </span>
                </label>
                <input
                  id="suggest-skill-job-title"
                  type="text"
                  value={suggestJobTitle}
                  onChange={(e) => setSuggestJobTitle(e.target.value)}
                  className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="e.g. Senior Software Engineer"
                  autoFocus
                />
              </div>

              {suggestSkills.error && (
                <div
                  className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
                  role="alert"
                >
                  {suggestSkills.error.message}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={
                    (!suggestJobTitle.trim() &&
                      !includeExperiences &&
                      !includeAccomplishments) ||
                    suggestSkills.isPending
                  }
                  className="inline-flex items-center gap-2 rounded-md bg-ai px-4 py-2 text-[15px] font-medium text-ai-foreground transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                >
                  {suggestSkills.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {suggestSkills.isPending
                    ? 'Generating...'
                    : 'Generate Suggestions'}
                </button>
              </div>
            </form>
          ) : (
            <div className="mt-3">
              <div className="flex items-center justify-between">
                <p className="text-[14px] text-muted-foreground">
                  {suggestions.length} suggestion
                  {suggestions.length !== 1 ? 's' : ''}{' '}
                  {suggestJobTitle.trim() && (
                    <>
                      for{' '}
                      <span className="font-medium text-foreground">
                        {suggestJobTitle}
                      </span>
                    </>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedSuggestions(
                        selectedSuggestions.size === suggestions.length
                          ? new Set()
                          : new Set(suggestions.map((_, i) => i)),
                      )
                    }
                    className="text-[13px] font-medium text-primary transition-colors hover:text-primary/80"
                  >
                    {selectedSuggestions.size === suggestions.length
                      ? 'Deselect all'
                      : 'Select all'}
                  </button>
                </div>
              </div>

              <div className="mt-3 max-h-[50vh] space-y-1.5 overflow-y-auto">
                {suggestions.map((suggestion, i) => (
                  <label
                    key={i}
                    className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2.5 transition-colors hover:bg-muted/50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSuggestions.has(i)}
                      onChange={() => toggleSuggestion(i)}
                      className="h-3.5 w-3.5 rounded border-input accent-primary"
                    />
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="text-[14px] font-medium text-foreground">
                        {suggestion.name}
                      </span>
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                        {CATEGORY_LABELS[suggestion.category] ?? suggestion.category}
                      </span>
                      {suggestion.proficiency && (
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            PROFICIENCY_COLORS[suggestion.proficiency] ??
                            'bg-muted text-muted-foreground'
                          }`}
                        >
                          {suggestion.proficiency.charAt(0).toUpperCase() +
                            suggestion.proficiency.slice(1)}
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                <button
                  type="button"
                  onClick={closeSuggestPanel}
                  className="rounded-md px-3 py-2 text-[14px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={handleAcceptSuggestions}
                  disabled={
                    selectedSuggestions.size === 0 || createSkill.isPending
                  }
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                >
                  {createSkill.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Add Selected ({selectedSuggestions.size})
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category filter */}
      <div className="mt-4 flex items-center gap-3">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {CATEGORY_LABELS[cat] ?? cat}
            </option>
          ))}
        </select>
        <span className="text-[14px] text-muted-foreground">
          {skillList.length} skill{skillList.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Bulk action bar */}
      {skillList.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <input
              type="checkbox"
              checked={bulk.allSelected}
              ref={(el) => {
                if (el) el.indeterminate = bulk.someSelected;
              }}
              onChange={bulk.toggleAll}
              className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-foreground"
            />
            {inSelectionMode
              ? `${bulk.count} of ${skillList.length}`
              : 'Select all'}
          </label>
          <BulkActionBar
            selectedCount={bulk.count}
            selectedIds={bulk.selectedArray}
            actions={bulkActions}
            onDeselectAll={bulk.deselectAll}
          />
        </div>
      )}

      {/* Create form — the edit form renders inline in place of the skill being edited */}
      {isFormOpen && !editingId && skillForm}

      {/* Skills grid grouped by category */}
      {skillList.length === 0 && !isFormOpen ? (
        <div className="mt-6 rounded-lg border border-dashed border-border p-12 text-center">
          <Wand2 className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-[15px] text-muted-foreground">
            {categoryFilter
              ? 'No skills match this category.'
              : 'No skills added yet. Add your technical, soft, and tool skills.'}
          </p>
          {!categoryFilter && (
            <button
              type="button"
              onClick={openCreateForm}
              className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-primary transition-colors hover:text-primary/80"
            >
              <Plus className="h-3.5 w-3.5" />
              Add your first skill
            </button>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-6">
          {groupedSkills.map(([category, categorySkills]) => (
            <div key={category}>
              <h3 className="mb-2 text-[14px] font-semibold uppercase tracking-wider text-muted-foreground">
                {CATEGORY_LABELS[category] ?? category}
                <span className="ml-2 font-normal">
                  ({categorySkills.length})
                </span>
              </h3>
              <div className="space-y-2">
                {categorySkills.map((skill) =>
                  editingId === skill.id ? (
                    <div key={skill.id} ref={editFormRef}>{skillForm}</div>
                  ) : (
                  <div
                    key={skill.id}
                    className={`group rounded-lg border bg-card p-3 transition-colors hover:border-border/80 ${bulk.isSelected(skill.id) ? 'border-primary/40 bg-primary/[0.02]' : 'border-border'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <SelectionCheckbox
                          checked={bulk.isSelected(skill.id)}
                          onChange={() => bulk.toggle(skill.id)}
                          visible={inSelectionMode}
                          ariaLabel={`Select ${skill.name}`}
                        />
                        <p className="text-[15px] font-medium text-foreground">
                          {skill.name}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {skill.proficiency && (
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[12px] font-medium ${
                                PROFICIENCY_COLORS[skill.proficiency] ??
                                'bg-muted text-muted-foreground'
                              }`}
                            >
                              {skill.proficiency.charAt(0).toUpperCase() +
                                skill.proficiency.slice(1)}
                            </span>
                          )}
                          {skill.years_experience !== undefined &&
                            skill.years_experience !== null && (
                              <span className="inline-flex rounded-full border border-border px-2 py-0.5 text-[12px] font-medium tabular-nums text-muted-foreground">
                                {skill.years_experience}{' '}
                                {skill.years_experience === 1
                                  ? 'year'
                                  : 'years'}
                              </span>
                            )}
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => openEditForm(skill)}
                          className="rounded-md p-2.5 sm:p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          aria-label={`Edit ${skill.name}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <InlineDeleteConfirm
                          onConfirm={() => handleDelete(skill.id)}
                          isDeleting={deletingId === skill.id}
                          ariaLabel={`Delete ${skill.name}`}
                        />
                      </div>
                    </div>
                  </div>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteSkill.error && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {deleteSkill.error.message}
        </div>
      )}

      <UnsavedChangesGuard {...unsaved.dialogProps} />
    </div>
  );
}
